import type { PurchaseRequest, PurchaseRequestStatus, Supplier } from "@kitchen/schemas";
import { Button, Chip, faDigits, Field, formatDate, formatMoney, Panel, Seg } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, purchasingApi, type StockItemRow, type WarehouseRow } from "../../api.js";
import { addDays, tehranToday } from "../../labels.js";
import { askReason, ErrorLine, useAction } from "../common.js";

/** Purchasing (requirements §8.6, §10.4): requests with the manager's decision, suppliers, bills and payments. */
type PurchasingTab = "requests" | "suppliers" | "money";

const STATUS: Record<
  PurchaseRequestStatus,
  { label: string; tone?: "blue" | "coral" | "ok" | "warn" | "dark" }
> = {
  draft: { label: "پیش‌نویس" },
  requested: { label: "منتظر تصمیم مدیر", tone: "warn" },
  approved: { label: "تأیید شد", tone: "blue" },
  declined: { label: "رد شد", tone: "coral" },
  received: { label: "دریافت شد", tone: "ok" },
  cancelled: { label: "لغو شد", tone: "dark" },
};

const METHOD: Record<string, string> = { cash: "نقد", card: "کارت", transfer: "حواله", cheque: "چک" };

/** User types kilograms (or the item's base unit) with decimals; the server keeps thousandths. */
const toMilli = (text: string) => Math.round(Number(text.replace(/[٬,]/g, "")) * 1000);
const toRial = (text: string) => Math.trunc(Number(text.replace(/[٬,]/g, "")) * 10); // typed in toman

export function PurchasingPage() {
  const [tab, setTab] = useState<PurchasingTab>("requests");
  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <div className="row wrap" style={{ alignItems: "center" }}>
        <Seg<PurchasingTab>
          label="بخش خرید و تأمین‌کنندگان"
          value={tab}
          onChange={setTab}
          options={[
            { value: "requests", label: "درخواست‌های خرید" },
            { value: "suppliers", label: "تأمین‌کنندگان" },
            { value: "money", label: "فاکتور و پرداخت" },
          ]}
        />
      </div>
      {tab === "requests" && <RequestsTab />}
      {tab === "suppliers" && <SuppliersTab />}
      {tab === "money" && <MoneyTab />}
    </div>
  );
}

function useSuppliers() {
  return useQuery({ queryKey: ["purchasing", "suppliers"], queryFn: () => purchasingApi.listSuppliers() });
}

function RequestsTab() {
  const requests = useQuery({
    queryKey: ["purchasing", "requests"],
    queryFn: () => purchasingApi.listRequests(),
  });
  const warehouses = useQuery({
    queryKey: ["inventory", "warehouses"],
    queryFn: () => api<WarehouseRow[]>("/inventory/warehouses"),
  });
  const items = useQuery({
    queryKey: ["inventory", "items"],
    queryFn: () => api<StockItemRow[]>("/inventory/items"),
  });
  const suppliers = useSuppliers();
  const itemName = new Map((items.data ?? []).map((i) => [i.id, `${i.name} (${i.baseUnit})`]));
  const supplierName = new Map((suppliers.data ?? []).map((s) => [s.id, s.name]));

  const [warehouseId, setWarehouseId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [neededBy, setNeededBy] = useState(addDays(tehranToday(), 2));
  const [lines, setLines] = useState([{ itemId: "", qty: "", price: "" }]);

  const refresh = [["purchasing", "requests"]];
  const create = useAction(async () => {
    await purchasingApi.createRequest({
      warehouseId,
      ...(supplierId ? { supplierId } : {}),
      neededBy,
      lines: lines
        .filter((l) => l.itemId && l.qty)
        .map((l) => ({
          itemId: l.itemId,
          quantityMilli: toMilli(l.qty),
          ...(l.price ? { expectedUnitPriceRial: toRial(l.price) } : {}),
        })),
    });
    setLines([{ itemId: "", qty: "", price: "" }]);
  }, refresh);
  const submit = useAction((r: PurchaseRequest) => purchasingApi.submitRequest(r.id, r.version), refresh);
  const decide = useAction(
    (a: { r: PurchaseRequest; decision: "approve" | "decline" }) =>
      purchasingApi.decide(
        a.r.id,
        a.r.version,
        a.decision,
        a.decision === "decline" ? (askReason("رد درخواست") ?? "") : "",
      ),
    refresh,
  );
  const receive = useAction(
    (r: PurchaseRequest) => purchasingApi.receive(r.id, r.version, tehranToday()),
    refresh,
  );
  const cancel = useAction(async (r: PurchaseRequest) => {
    const reason = askReason("لغو درخواست خرید");
    if (reason) await purchasingApi.cancelRequest(r.id, r.version, reason);
  }, refresh);

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <Panel title="درخواست خرید تازه">
        <div className="stack" style={{ gap: "var(--s3)" }}>
          <div className="row wrap" style={{ gap: "var(--s3)" }}>
            <label className="field">
              <span>انبار</span>
              <select
                className="input"
                value={warehouseId}
                onChange={(e) => {
                  setWarehouseId(e.target.value);
                }}
              >
                <option value="">انتخاب انبار</option>
                {(warehouses.data ?? []).map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>تأمین‌کننده (اختیاری)</span>
              <select
                className="input"
                value={supplierId}
                onChange={(e) => {
                  setSupplierId(e.target.value);
                }}
              >
                <option value="">هنوز معلوم نیست</option>
                {(suppliers.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <Field
              id="pr-needed"
              label="لازم تا تاریخ"
              type="date"
              value={neededBy}
              onChange={(e) => {
                setNeededBy(e.target.value);
              }}
            />
          </div>
          {lines.map((l, i) => (
            <div className="row wrap" style={{ gap: "var(--s3)" }} key={i}>
              <label className="field">
                <span>کالا</span>
                <select
                  className="input"
                  aria-label={`کالای ردیف ${faDigits(String(i + 1))}`}
                  value={l.itemId}
                  onChange={(e) => {
                    setLines(lines.map((x, j) => (j === i ? { ...x, itemId: e.target.value } : x)));
                  }}
                >
                  <option value="">انتخاب کالا</option>
                  {(items.data ?? []).map((it) => (
                    <option key={it.id} value={it.id}>
                      {it.name} ({it.baseUnit})
                    </option>
                  ))}
                </select>
              </label>
              <Field
                id={`pr-qty-${String(i)}`}
                label="مقدار"
                inputMode="decimal"
                value={l.qty}
                onChange={(e) => {
                  setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)));
                }}
              />
              <Field
                id={`pr-price-${String(i)}`}
                label="قیمت تخمینی هر واحد (تومان)"
                inputMode="numeric"
                value={l.price}
                onChange={(e) => {
                  setLines(lines.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)));
                }}
              />
            </div>
          ))}
          <div className="row" style={{ gap: "var(--s2)" }}>
            <Button
              variant="ghost"
              onClick={() => {
                setLines([...lines, { itemId: "", qty: "", price: "" }]);
              }}
            >
              ردیف دیگر
            </Button>
            <Button
              variant="primary"
              disabled={!warehouseId || !lines.some((l) => l.itemId && l.qty) || create.busy}
              onClick={() => {
                create.run(undefined);
              }}
            >
              ذخیره‌ی پیش‌نویس درخواست
            </Button>
          </div>
          <ErrorLine error={create.error} />
        </div>
      </Panel>

      <Panel title="درخواست‌ها">
        <ErrorLine error={submit.error ?? decide.error ?? receive.error ?? cancel.error} />
        {requests.isLoading ? (
          <p className="muted">در حال بارگیری…</p>
        ) : requests.isError ? (
          <p role="alert">بارگیری نشد</p>
        ) : (requests.data ?? []).length === 0 ? (
          <p className="muted">هنوز درخواست خریدی ثبت نشده است.</p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>لازم تا</th>
                <th>اقلام</th>
                <th>تأمین‌کننده</th>
                <th>جمع تخمینی</th>
                <th>وضعیت</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(requests.data ?? []).map((r) => (
                <tr key={r.id}>
                  <td>{formatDate(r.neededBy)}</td>
                  <td>
                    {r.lines
                      .map(
                        (l) =>
                          `${itemName.get(l.itemId) ?? "کالا"} × ${faDigits(String(l.quantityMilli / 1000))}`,
                      )
                      .join("، ")}
                  </td>
                  <td>{r.supplierId ? (supplierName.get(r.supplierId) ?? "—") : "—"}</td>
                  <td className="num">{r.expectedTotalRial ? formatMoney(r.expectedTotalRial) : "—"}</td>
                  <td>
                    <Chip tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Chip>
                  </td>
                  <td className="end">
                    <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
                      {r.status === "draft" && (
                        <Button
                          variant="primary"
                          onClick={() => {
                            submit.run(r);
                          }}
                        >
                          فرستادن برای مدیر
                        </Button>
                      )}
                      {r.status === "requested" && (
                        <>
                          <Button
                            variant="primary"
                            onClick={() => {
                              decide.run({ r, decision: "approve" });
                            }}
                          >
                            تأیید
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              decide.run({ r, decision: "decline" });
                            }}
                          >
                            رد
                          </Button>
                        </>
                      )}
                      {r.status === "approved" && (
                        <Button
                          variant="primary"
                          onClick={() => {
                            receive.run(r);
                          }}
                        >
                          کالا رسید (رسید پیش‌نویس انبار)
                        </Button>
                      )}
                      {(r.status === "draft" || r.status === "requested" || r.status === "approved") && (
                        <Button
                          variant="ghost"
                          onClick={() => {
                            cancel.run(r);
                          }}
                        >
                          لغو
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}

function SuppliersTab() {
  const suppliers = useSuppliers();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const refresh = [["purchasing", "suppliers"]];
  const add = useAction(async () => {
    await purchasingApi.createSupplier({ name: name.trim(), phone: phone.trim() });
    setName("");
    setPhone("");
  }, refresh);
  const archive = useAction(async (s: Supplier) => {
    const reason = askReason(`تأمین‌کننده ${s.name}`);
    if (reason) await purchasingApi.archiveSupplier(s.id, s.version, reason);
  }, refresh);

  return (
    <Panel title="تأمین‌کنندگان">
      <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "flex-end" }}>
        <Field
          id="sup-name"
          label="نام"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
        <Field
          id="sup-phone"
          label="تلفن"
          inputMode="tel"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
          }}
        />
        <Button
          variant="primary"
          disabled={!name.trim() || add.busy}
          onClick={() => {
            add.run(undefined);
          }}
        >
          افزودن تأمین‌کننده
        </Button>
      </div>
      <ErrorLine error={add.error ?? archive.error} />
      {(suppliers.data ?? []).length === 0 ? (
        <p className="muted">هنوز تأمین‌کننده‌ای ثبت نشده است.</p>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>نام</th>
              <th>تلفن</th>
              <th>مانده (بدهی ما)</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(suppliers.data ?? []).map((s) => (
              <tr key={s.id}>
                <td className="bold">{s.name}</td>
                <td className="num">{s.phone ? faDigits(s.phone) : "—"}</td>
                <td className="num">{formatMoney(s.balanceRial)}</td>
                <td className="end">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      archive.run(s);
                    }}
                  >
                    بایگانی
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function MoneyTab() {
  const suppliers = useSuppliers();
  const bills = useQuery({ queryKey: ["purchasing", "bills"], queryFn: () => purchasingApi.listBills() });
  const payments = useQuery({
    queryKey: ["purchasing", "payments"],
    queryFn: () => purchasingApi.listPayments(),
  });
  const supplierName = new Map((suppliers.data ?? []).map((s) => [s.id, s.name]));
  const [supplierId, setSupplierId] = useState("");
  const [billNumber, setBillNumber] = useState("");
  const [billTotal, setBillTotal] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [method, setMethod] = useState<"cash" | "card" | "transfer" | "cheque">("transfer");
  const [chequeNumber, setChequeNumber] = useState("");
  const [chequeDue, setChequeDue] = useState("");
  const [from, setFrom] = useState(addDays(tehranToday(), -30));
  const [to, setTo] = useState(tehranToday());
  const statement = useQuery({
    queryKey: ["purchasing", "statement", supplierId, from, to],
    queryFn: () => purchasingApi.statement(supplierId, from, to),
    enabled: !!supplierId,
  });
  const refresh = [["purchasing"]];
  const bill = useAction(async () => {
    await purchasingApi.recordBill({
      supplierId,
      billNumber: billNumber.trim(),
      billDate: tehranToday(),
      totalRial: toRial(billTotal),
    });
    setBillNumber("");
    setBillTotal("");
  }, refresh);
  const pay = useAction(async () => {
    await purchasingApi.recordPayment({
      supplierId,
      paidOn: tehranToday(),
      amountRial: toRial(payAmount),
      method,
      ...(method === "cheque" ? { chequeNumber, chequeDueDate: chequeDue } : {}),
    });
    setPayAmount("");
  }, refresh);

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <Panel title="تأمین‌کننده">
        <label className="field">
          <span>تأمین‌کننده</span>
          <select
            className="input"
            value={supplierId}
            onChange={(e) => {
              setSupplierId(e.target.value);
            }}
          >
            <option value="">انتخاب تأمین‌کننده</option>
            {(suppliers.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      </Panel>

      {supplierId && (
        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "var(--s4)" }}
        >
          <Panel title="ثبت فاکتور خرید">
            <Field
              id="bill-no"
              label="شماره‌ی فاکتور تأمین‌کننده"
              value={billNumber}
              onChange={(e) => {
                setBillNumber(e.target.value);
              }}
            />
            <Field
              id="bill-total"
              label="جمع فاکتور (تومان)"
              inputMode="numeric"
              value={billTotal}
              onChange={(e) => {
                setBillTotal(e.target.value);
              }}
            />
            <Button
              variant="primary"
              disabled={!billNumber.trim() || !billTotal || bill.busy}
              onClick={() => {
                bill.run(undefined);
              }}
            >
              ثبت فاکتور
            </Button>
            <ErrorLine error={bill.error} />
          </Panel>
          <Panel title="ثبت پرداخت">
            <Field
              id="pay-amount"
              label="مبلغ (تومان)"
              inputMode="numeric"
              value={payAmount}
              onChange={(e) => {
                setPayAmount(e.target.value);
              }}
            />
            <label className="field">
              <span>روش</span>
              <select
                className="input"
                value={method}
                onChange={(e) => {
                  setMethod(e.target.value as typeof method);
                }}
              >
                {Object.entries(METHOD).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            {method === "cheque" && (
              <>
                <Field
                  id="pay-cheque"
                  label="شماره چک"
                  value={chequeNumber}
                  onChange={(e) => {
                    setChequeNumber(e.target.value);
                  }}
                />
                <Field
                  id="pay-due"
                  label="سررسید"
                  type="date"
                  value={chequeDue}
                  onChange={(e) => {
                    setChequeDue(e.target.value);
                  }}
                />
              </>
            )}
            <Button
              variant="primary"
              disabled={!payAmount || pay.busy}
              onClick={() => {
                pay.run(undefined);
              }}
            >
              ثبت پرداخت
            </Button>
            <ErrorLine error={pay.error} />
          </Panel>
        </div>
      )}

      {supplierId && (
        <Panel title="صورت‌حساب تأمین‌کننده">
          <div className="row wrap" style={{ gap: "var(--s3)" }}>
            <Field
              id="st-from"
              label="از"
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
              }}
            />
            <Field
              id="st-to"
              label="تا"
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
              }}
            />
          </div>
          {statement.isError ? (
            <p role="alert">بارگیری نشد</p>
          ) : statement.data ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th>تاریخ</th>
                  <th>شرح</th>
                  <th>بدهکار (فاکتور)</th>
                  <th>بستانکار (پرداخت)</th>
                  <th>مانده</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={4} className="muted">
                    مانده‌ی ابتدای دوره
                  </td>
                  <td className="num">{formatMoney(statement.data.openingRial)}</td>
                </tr>
                {statement.data.entries.map((e) => (
                  <tr key={e.id}>
                    <td>{formatDate(e.date)}</td>
                    <td>
                      {e.kind === "bill"
                        ? `فاکتور ${faDigits(e.reference)}`
                        : `پرداخت ${faDigits(e.reference)}`}
                    </td>
                    <td className="num">{e.debitRial ? formatMoney(e.debitRial) : "—"}</td>
                    <td className="num">{e.creditRial ? formatMoney(e.creditRial) : "—"}</td>
                    <td className="num">{formatMoney(e.balanceRial)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="muted">در حال بارگیری…</p>
          )}
        </Panel>
      )}

      <Panel title="آخرین فاکتورها و پرداخت‌ها">
        <p className="muted">
          {faDigits(String((bills.data ?? []).length))} فاکتور،{" "}
          {faDigits(String((payments.data ?? []).length))} پرداخت
          {supplierId ? ` — ${supplierName.get(supplierId) ?? ""}` : ""}
        </p>
      </Panel>
    </div>
  );
}
